import React, { SyntheticEvent, useEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { getCodeData } from "../utils/CommonUtils";
import {
  Autocomplete,
  Box,
  MenuItem,
  Popper,
  Stack,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  DateCalendar,
  DatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { StyleSettings } from "../../styles/StyleSettings";

interface SnComboPeriodBoxProps {
  defaultValue?: any;
  code?: string;
  sql?: string;
  emptyLine?: boolean;
  onValueChanged?: (combo: string, dt_start: string, dt_end: string) => void;
  isReadOnly?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  isFormPanel?: boolean;
  js_combo?: any[] | string;
  // comboValue 가 이 목록(CSV)에 속하면 단일일자 모드로 렌더하고 start=end 로 방출한다. (예: "3")
  singleDateCodes?: string;
}

const SnComboPeriodBox = React.memo(
  ({
    defaultValue,
    code,
    sql,
    emptyLine,
    onValueChanged,
    isReadOnly = false,
    inputRef,
    isSearchPanel = false,
    isFormPanel = false,
    js_combo,
    singleDateCodes,
  }: SnComboPeriodBoxProps) => {
    // combo 세팅
    const defaultValueCombo: string = defaultValue
      ? defaultValue.split(",")[0].trim()
      : "";
    const [options, setOptions] = useState<any[]>([]);
    const [comboValue, setComboValue] = useState<string>(defaultValueCombo);

    // 단일일자 모드 판별 (예: 재고기준일='3')
    const isSingleCode = (code: string) =>
      (singleDateCodes ?? "")
        .split(",")
        .map((c) => c.trim())
        .filter((c) => c !== "")
        .includes(code);
    const isSingle = isSingleCode(comboValue);

    // period 세팅
    const start: string = defaultValue ? defaultValue.split(",")[1].trim() : "";
    const end: string = defaultValue ? defaultValue.split(",")[2].trim() : "";
    const [periodValue, setPeriodValue] = useState<
      [Dayjs | null, Dayjs | null]
    >([dayjs(start || new Date()), dayjs(end || new Date())]);

    const [isPopperOpen, setIsPopperOpen] = useState(false);
    const firstTextFieldRef = useRef<HTMLElement | null>(null);
    const secondRef = useRef<HTMLDivElement | null>(null);

    const fetchDefaultValue = async () => {
      try {
        if (defaultValue !== undefined) {
          const defaultValueCombo: string = defaultValue.split(",")[0].trim();
          const start: string = defaultValue.split(",")[1].trim();
          const end: string = defaultValue.split(",")[2].trim();
          const defaultValuePeriod: [Dayjs, Dayjs] = [
            dayjs(start || new Date()),
            dayjs(end || new Date()),
          ];
          setComboValue(defaultValueCombo);
          setPeriodValue(defaultValuePeriod);
        } else {
          setComboValue("");
          setPeriodValue([dayjs(new Date()), dayjs(new Date())]);
        }
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      const fetchData = async () => {
        try {
          const dataSource = Array.isArray(js_combo)
            ? js_combo
            : typeof js_combo === "string"
              ? JSON.parse(js_combo)
              : [];
          const data =
            dataSource.length > 0
              ? dataSource
              : await getCodeData(code || "", sql || "", emptyLine);
          setOptions(Array.isArray(data) ? data : data ? [data] : []);
          if (comboValue.toLowerCase() === "top1") {
            if (Array.isArray(data) && data.length > 0) {
              const firstCode = data[0].code;
              setComboValue(firstCode);
              if (onValueChanged) {
                let dt_start: string = "";
                let dt_end: string = "";
                if (periodValue[0] !== null) {
                  dt_start = periodValue[0].format("YYYYMMDD");
                }
                if (periodValue[1] !== null) {
                  dt_end = periodValue[1].format("YYYYMMDD");
                }
                onValueChanged(firstCode, dt_start, dt_end);
              }
            }
          }
        } catch (error) {
          console.error(error);
        }
      };
      fetchData();
    }, [code, sql, emptyLine]);

    const handleComboChange = (e: SyntheticEvent, newValue: any) => {
      const updatedValue = newValue?.code || null;
      setComboValue(updatedValue);

      if (onValueChanged) {
        let dt_start: string = "";
        let dt_end: string = "";
        if (periodValue[0] !== null) {
          dt_start = periodValue[0].format("YYYYMMDD");
        }
        if (periodValue[1] !== null) {
          dt_end = periodValue[1].format("YYYYMMDD");
        }
        // 단일일자 코드로 전환되면 종료일을 시작일과 동일하게 맞춘다.
        if (updatedValue && isSingleCode(updatedValue)) {
          dt_end = dt_start;
          setPeriodValue([periodValue[0], periodValue[0]]);
        }
        onValueChanged(updatedValue, dt_start, dt_end);
      }
    };

    const handleSingleDateChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      setPeriodValue([newValue, newValue]);
      const d = newValue.format("YYYYMMDD");
      if (onValueChanged) onValueChanged(comboValue, d, d);
    };

    const handleOpen = () => {
      if (!isReadOnly) {
        setIsPopperOpen(true);
      }
    };

    const handleClose = () => {
      setIsPopperOpen(false);
      secondRef.current?.focus();
    };

    const returnData = (startDate: Dayjs | null, endDate: Dayjs | null) => {
      let start: string = "";
      let end: string = "";
      if (startDate !== null) start = startDate.format("YYYY-MM-DD");
      if (endDate !== null) end = endDate.format("YYYY-MM-DD");
      return { start, end };
    };

    const emitChange = (startDate: Dayjs | null, endDate: Dayjs | null) => {
      setPeriodValue([startDate, endDate]);
      const date = returnData(startDate, endDate);
      if (onValueChanged) {
        onValueChanged(
          comboValue,
          date.start.replace(/-/g, ""),
          date.end.replace(/-/g, "")
        );
      }
    };

    // 텍스트 입력: 기존과 동일하게 반대편 날짜를 건드리지 않음
    const handleStartDateChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      emitChange(newValue, periodValue[1]);
    };

    const handleEndDateChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      emitChange(periodValue[0], newValue);
      handleClose();
    };

    // 달력 클릭: 시작일이 종료일보다 뒤면 종료일을 시작일과 같은 날로 맞춤
    const handleStartCalendarChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      const endDate =
        periodValue[1] && newValue.isAfter(periodValue[1], "day")
          ? newValue
          : periodValue[1];
      emitChange(newValue, endDate);
    };

    // 달력 클릭: 종료일이 시작일보다 앞이면 시작일을 종료일과 같은 날로 맞춤
    const handleEndCalendarChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      const startDate =
        periodValue[0] && newValue.isBefore(periodValue[0], "day")
          ? newValue
          : periodValue[0];
      emitChange(startDate, newValue);
      handleClose();
    };

    // 날짜 더블클릭 시 Popper 닫기
    const handleDateDoubleClick = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      handleClose();
    };

    // 외부 클릭 감지
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          firstTextFieldRef.current &&
          !firstTextFieldRef.current.contains(event.target as Node) &&
          !document
            .getElementById("calendar-popper")
            ?.contains(event.target as Node)
        ) {
          handleClose();
        }
      };

      if (isPopperOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isPopperOpen]);

    return (
      <div>
        <Stack direction="row" alignItems="center">
          <Autocomplete
            disablePortal={!isSearchPanel && !isFormPanel}
            options={options}
            disabled={isReadOnly}
            value={
              options.find((option: any) => option.code === comboValue) || null
            }
            onChange={handleComboChange}
            getOptionLabel={(option: any) => option.name}
            disableClearable={true}
            isOptionEqualToValue={(option: any, value: any) =>
              option.code === value?.code
            }
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={inputRef}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: StyleSettings.font,
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <MenuItem
                {...props}
                key={option.code}
                sx={{
                  fontSize: StyleSettings.font,
                  minHeight: "32px",
                }}
              >
                {option.name || " "}
              </MenuItem>
            )}
            sx={{
              width: 110,
              flexShrink: 0,
              marginRight: "10px",
              backgroundColor: "white",
              "& .MuiInputBase-root": {
                height: StyleSettings.control_height,
              },
            }}
            slotProps={{
              listbox: {
                style: {
                  maxHeight: 250,
                  overflow: "auto",
                },
              },
            }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            {isSingle ? (
              <div style={{ flex: 1 }}>
                <DatePicker
                  value={periodValue[0]}
                  format={"YYYY/MM/DD"}
                  onChange={handleSingleDateChange}
                  disabled={isReadOnly}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        "& .MuiInputBase-root": {
                          height: StyleSettings.control_height,
                          fontSize: StyleSettings.font,
                        },
                        backgroundColor: "white",
                      },
                    },
                  }}
                />
              </div>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                    flex: 1,
                  }}
                >
                  <div style={{ width: "100%" }}>
                    <DatePicker
                      value={periodValue[0]}
                      format={"YYYY/MM/DD"}
                      onChange={handleStartDateChange}
                      disabled={isReadOnly}
                      slotProps={{
                        textField: {
                          onClick: handleOpen,
                          inputRef: firstTextFieldRef,
                          fullWidth: true,
                          sx: {
                            "& .MuiInputBase-root": {
                              height: StyleSettings.control_height,
                              fontSize: StyleSettings.font,
                            },
                            backgroundColor: "white",
                          },
                          InputProps: {
                            endAdornment: null, // 달력 아이콘 제거
                          },
                        },
                      }}
                    />
                  </div>
                  <Box> – </Box>
                  <div
                    onKeyDown={(e) => {
                      if (e.key === "Tab") {
                        handleClose();
                      }
                    }}
                    style={{ width: "100%" }}
                  >
                    <DatePicker
                      value={periodValue[1]}
                      format={"YYYY/MM/DD"}
                      onChange={handleEndDateChange}
                      disabled={isReadOnly}
                      slotProps={{
                        textField: {
                          onClick: handleOpen,
                          fullWidth: true,
                          inputRef: secondRef,
                          sx: {
                            "& .MuiInputBase-root": {
                              height: StyleSettings.control_height,
                              fontSize: StyleSettings.font,
                            },
                            backgroundColor: "white",
                          },
                          InputProps: {
                            endAdornment: null, // 달력 아이콘 제거
                          },
                        },
                      }}
                    />
                  </div>
                </Box>
                <Popper
                  id="calendar-popper"
                  open={isPopperOpen}
                  anchorEl={firstTextFieldRef.current} // 항상 첫 번째 DatePicker 기준으로 고정
                  placement="bottom-start"
                  modifiers={[
                    {
                      name: "offset",
                      options: {
                        offset: [0, -8],
                      },
                    },
                  ]}
                  sx={{
                    boxShadow:
                      "0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    zIndex: 1500,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      p: 2,
                      borderRadius: "8px",
                      backgroundColor: "white",
                      "& .MuiDateCalendar-root": {
                        height: "auto",
                      },
                      "& .MuiPickersCalendarHeader-root": {
                        marginTop: 0,
                      },
                    }}
                  >
                    <DateCalendar
                      value={periodValue[0]}
                      onChange={handleStartCalendarChange}
                      sx={{
                        width: 300,
                        "& .MuiPickersSlideTransition-root": {
                          minHeight: "205px",
                        },
                      }}
                      slotProps={{
                        day: {
                          onDoubleClickCapture: (event) =>
                            handleDateDoubleClick(event),
                        },
                      }}
                    />
                    <DateCalendar
                      value={periodValue[1]}
                      onChange={handleEndCalendarChange}
                      sx={{
                        width: 300,
                        "& .MuiPickersSlideTransition-root": {
                          minHeight: "205px",
                        },
                      }}
                    />
                  </Box>
                </Popper>
              </>
            )}
          </LocalizationProvider>
        </Stack>
      </div>
    );
  }
);

export default SnComboPeriodBox;
